/**
 * useSubscription — web hook, reads the pieces the resolver needs from
 * the active user context + Firestore and returns the resolved premium status.
 *
 * Re-runs when schoolId or role changes (context-aware per the model doc).
 *
 * While MONETIZATION_ENABLED is false, this hook returns a stub that reports
 * everyone as free — callers see the same shape they'll see post-launch, but
 * gating is a no-op (caller-side behaviour is controlled by <PremiumGate>).
 */

import { useEffect, useState, useMemo } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../../firebase';
import { useAuth } from '../../auth/hooks/useAuth';
import { useSchool } from '../../../contexts/SchoolContext';
import { useRoleSwitcher } from '../../auth/hooks/useRoleSwitcher';
import { resolvePremium, satisfies } from '../resolvePremium';
import { getFeature } from '../features';
import { MONETIZATION_ENABLED } from '../../../config/featureFlags';

const FREE_STATUS = {
  isPremium: false,
  source: null,
  schoolId: null,
  tier: 'free',
  hasAi: false,
};

export function useSubscription() {
  const { user } = useAuth();
  const { schoolId } = useSchool();
  const { currentRole } = useRoleSwitcher();

  const [schoolSub, setSchoolSub] = useState(null);
  const [userSeat, setUserSeat] = useState(null);
  const [loading, setLoading] = useState(MONETIZATION_ENABLED);

  useEffect(() => {
    if (!MONETIZATION_ENABLED || !schoolId) {
      setSchoolSub(null);
      setUserSeat(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    const subRef = doc(db, 'schools', schoolId, 'subscription', 'current');
    const unsubSub = onSnapshot(subRef, (snap) => {
      setSchoolSub(snap.exists() ? snap.data() : null);
    });

    const seatRef = user?.uid
      ? doc(db, 'schools', schoolId, 'seats', user.uid)
      : null;
    const unsubSeat = seatRef
      ? onSnapshot(seatRef, (snap) => {
          setUserSeat(snap.exists() ? snap.data() : null);
          setLoading(false);
        })
      : (() => { setLoading(false); return () => {}; })();

    return () => {
      unsubSub();
      if (typeof unsubSeat === 'function') unsubSeat();
    };
  }, [schoolId, user?.uid]);

  const status = useMemo(() => {
    if (!MONETIZATION_ENABLED) return FREE_STATUS;
    return resolvePremium({
      schoolId,
      role: currentRole || 'teacher',
      customerInfo: null, // web has no individual sub; RevenueCat lives on mobile
      schoolSub,
      userSeat,
    });
  }, [schoolId, currentRole, schoolSub, userSeat]);

  return { status, loading };
}

/**
 * Hook that returns a boolean for a specific feature key.
 * When MONETIZATION_ENABLED is false, always returns true (flag-off means
 * no gating active in the UI — the point of the launch posture).
 */
export function useCanUse(featureKey) {
  const { status } = useSubscription();
  if (!MONETIZATION_ENABLED) return true;
  const feature = getFeature(featureKey);
  if (!feature) {
    if (import.meta.env.DEV) {
      console.warn(`[useCanUse] unknown feature: ${featureKey} — add to features.js`);
    }
    return true;
  }
  return satisfies(feature.requires, status);
}
